import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Terminal, Check, Loader2 } from "lucide-react";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const APP_TYPES = [
  { id: "website", label: "Website", base: 499, days: 1, baseScreens: 3, blurb: "Marketing site / landing page" },
  { id: "ai_app", label: "AI App (Janani-style)", base: 1999, days: 7, baseScreens: 6, blurb: "Full-stack AI product" },
  { id: "ai_agent", label: "AI Agent", base: 1999, days: 7, baseScreens: 4, blurb: "Autonomous workflow agent" },
  { id: "ace_ai", label: "ACE AI App", base: 2999, days: 10, baseScreens: 8, blurb: "Domain-specific AI suite" },
];

const FEATURES = [
  { id: "ai", label: "AI / LLM Integration", price: 600, days: 2 },
  { id: "auth", label: "User Authentication", price: 300, days: 1 },
  { id: "payments", label: "Payments (Stripe)", price: 400, days: 1 },
  { id: "admin", label: "Admin Dashboard", price: 500, days: 2 },
  { id: "i18n", label: "Multi-language", price: 300, days: 1 },
  { id: "notify", label: "Push / Email Notifications", price: 250, days: 1 },
  { id: "brand", label: "Custom Branding & Logo", price: 400, days: 1 },
];

const PER_SCREEN = 80;

export default function Estimator() {
  const [appType, setAppType] = useState(APP_TYPES[1]);
  const [features, setFeatures] = useState(["ai"]);
  const [screens, setScreens] = useState(6);
  const [rush, setRush] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });

  const estimate = useMemo(() => {
    const lineItems = [{ label: `${appType.label} (base)`, price: appType.base, days: appType.days }];
    const extraScreens = Math.max(0, screens - appType.baseScreens);
    if (extraScreens > 0) {
      lineItems.push({ label: `${extraScreens} extra screen(s)`, price: extraScreens * PER_SCREEN, days: extraScreens * 0.3 });
    }
    FEATURES.filter((f) => features.includes(f.id)).forEach((f) =>
      lineItems.push({ label: f.label, price: f.price, days: f.days })
    );

    let price = lineItems.reduce((a, b) => a + b.price, 0);
    let days = lineItems.reduce((a, b) => a + b.days, 0);

    if (rush) {
      price = Math.round(price * 1.3);
      days = Math.max(1, Math.ceil(days * 0.6));
    } else {
      days = Math.ceil(days);
    }
    return { lineItems, price, days };
  }, [appType, features, screens, rush]);

  const toggleFeature = (id) => {
    setFeatures((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/project-request", {
        ...form,
        app_type: appType.label,
        speed: rush ? "rush" : "standard",
        screens,
        features: FEATURES.filter((f) => features.includes(f.id)).map((f) => f.label),
        line_items: estimate.lineItems,
        total_price: estimate.price,
        total_days: estimate.days,
      });
      toast.success("Estimate submitted! We'll reach out shortly.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="py-24 relative border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-neon-cyan mb-3">// build your app</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Configure it. See the <span className="text-neon-yellow">price</span> &amp;{" "}
            <span className="text-neon-cyan">timeline</span> live.
          </h2>
          <p className="mt-4 text-white/55">Transparent, lean pricing. No surprises, no bloat.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Configurator */}
          <div className="lg:col-span-2 rounded-2xl border border-neon-cyan/15 bg-surface p-6 sm:p-8">
            {/* App type */}
            <Label className="font-mono text-xs uppercase tracking-wider text-white/50">App type</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {APP_TYPES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`estimator-type-${t.id}`}
                  onClick={() => {
                    setAppType(t);
                    setScreens(t.baseScreens);
                  }}
                  className={`text-left rounded-xl border p-3 transition-all ${
                    appType.id === t.id
                      ? "border-neon-cyan bg-neon-cyan/10 shadow-[0_0_18px_rgba(0,255,156,0.25)]"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="text-sm font-semibold">{t.label}</div>
                  <div className="text-[11px] text-white/45 mt-1">{t.blurb}</div>
                </button>
              ))}
            </div>

            {/* Screens slider */}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <Label className="font-mono text-xs uppercase tracking-wider text-white/50">Screens / pages</Label>
                <span data-testid="estimator-screens-value" className="font-mono text-neon-cyan font-bold">{screens}</span>
              </div>
              <Slider
                data-testid="estimator-slider-screens"
                value={[screens]}
                min={1}
                max={20}
                step={1}
                onValueChange={(v) => setScreens(v[0])}
                className="mt-4"
              />
            </div>

            {/* Features */}
            <div className="mt-8">
              <Label className="font-mono text-xs uppercase tracking-wider text-white/50">Features</Label>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <label
                    key={f.id}
                    data-testid={`estimator-feature-${f.id}`}
                    className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3 cursor-pointer hover:border-white/30 transition-colors"
                  >
                    <div>
                      <div className="text-sm font-medium">{f.label}</div>
                      <div className="font-mono text-[11px] text-white/40">+${f.price} · +{f.days}d</div>
                    </div>
                    <Switch checked={features.includes(f.id)} onCheckedChange={() => toggleFeature(f.id)} />
                  </label>
                ))}
              </div>
            </div>

            {/* Rush */}
            <div className="mt-6 flex items-center justify-between rounded-xl border border-neon-yellow/30 bg-neon-yellow/5 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-neon-yellow">Rush delivery</div>
                <div className="text-[11px] text-white/50">~40% faster · +30% cost</div>
              </div>
              <Switch data-testid="estimator-rush-toggle" checked={rush} onCheckedChange={setRush} />
            </div>
          </div>

          {/* Live terminal summary */}
          <div className="rounded-2xl border border-neon-cyan/30 bg-[#07070b] p-0 overflow-hidden h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 bg-surface">
              <Terminal size={15} className="text-neon-cyan" />
              <span className="font-mono text-xs text-white/60">estimate.sh</span>
              <div className="ml-auto flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                <span className="w-2.5 h-2.5 rounded-full bg-neon-cyan/60" />
              </div>
            </div>
            <div className="p-5 font-mono text-xs space-y-1.5">
              {estimate.lineItems.map((li, i) => (
                <div key={i} className="flex justify-between gap-3 text-white/60">
                  <span className="truncate">{li.label}</span>
                  <span className="text-white/80 whitespace-nowrap">${li.price.toLocaleString()}</span>
                </div>
              ))}
              {rush && <div className="text-neon-yellow pt-1">+ rush multiplier applied</div>}
              <div className="border-t border-white/10 my-3" />
              <div className="flex justify-between items-end">
                <span className="text-white/50 uppercase tracking-wider">Total</span>
                <motion.span
                  key={estimate.price}
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: 1 }}
                  data-testid="estimator-total-price"
                  className="text-3xl font-bold text-neon-cyan"
                >
                  ${estimate.price.toLocaleString()}
                </motion.span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-white/50 uppercase tracking-wider">Timeline</span>
                <span data-testid="estimator-total-days" className="text-neon-yellow font-bold">
                  ~{estimate.days} day{estimate.days > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="p-5 pt-0">
              <button
                data-testid="estimator-submit-open"
                onClick={() => setOpen(true)}
                className="neon-btn w-full bg-neon-cyan text-black font-semibold py-3 rounded-xl"
              >
                Request this build
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border border-neon-cyan/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Lock in your build</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              Send us your details and we'll confirm scope, price and timeline within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="font-mono text-xs text-white/60 border border-white/10 rounded-lg p-3 flex justify-between">
              <span>{appType.label} · {estimate.days}d</span>
              <span className="text-neon-cyan font-bold">${estimate.price.toLocaleString()}</span>
            </div>
            <div>
              <Label className="text-xs text-white/60">Name *</Label>
              <Input data-testid="estimator-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-void border-white/15 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Email *</Label>
              <Input data-testid="estimator-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-void border-white/15 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-white/60">Phone</Label>
                <Input data-testid="estimator-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-void border-white/15 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-white/60">Company</Label>
                <Input data-testid="estimator-form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-void border-white/15 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/60">Anything else?</Label>
              <Textarea data-testid="estimator-form-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-void border-white/15 mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="estimator-form-submit"
              onClick={submit}
              disabled={submitting}
              className="neon-btn w-full bg-neon-cyan text-black font-semibold py-3 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
              Submit estimate
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
